import { FC, useMemo } from "react";
import {
  MuiDoubleLabel,
  MuiGrid,
} from "../../../../../../../../../../components";
import { getFieldRuleByCountryForLandingInformation } from "../../../../../../../../../../helpers/constants/countryFieldRulesUtils";
import {
  convertToUserFormatWithLocalTime,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../../../hooks/useGridContainer";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import {
  EmployerInformationType,
  OccupationalFunctionOtherKey,
  ProgramClosureFormKey,
} from "../../../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../../siteCore/SiteCoreKeys";

interface EmployeeInformationProps {
  pageId: string;
  userCountryDateFormat?: string;
  employerInformation?: EmployerInformationType;
  deliveryCountry?: string;
}

export const EmployeeInformation: FC<EmployeeInformationProps> = ({
  pageId,
  userCountryDateFormat,
  employerInformation,
  deliveryCountry = "",
}) => {
  const {
    fetchLabel,
    fetchValueByKeyOfDropdown,
    fetchDropdownListKeyValueForCountrySpecific,
  } = useSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.changeStatusModelPopupKey, key);
  };

  const isFunctionalChange =
    employerInformation?.functionalChange ===
    programSiteCoreKeys.functionalChangeYes;

  const isNewOccupationalFunctionOther =
    employerInformation?.newOccupationalFunction ===
    OccupationalFunctionOtherKey;

  const isIndustryChange =
    employerInformation?.industryChange ===
    programSiteCoreKeys.industryChangeYes;

  const newEmployerVATNumberRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.newEmployerVATNumber,
  );

  const currentSalaryRangeRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.currentSalaryRange,
  );

  const newSalaryRangeRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.newSalaryRange,
  );

  const newLevelRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.newLevel,
  );

  const countrySpecificParticipantDetail = useMemo(() => {
    const currentSalaryRange = fetchValueByKeyOfDropdown(
      programSiteCoreKeys.changeStatusModelPopupKey,
      programSiteCoreKeys.currentSalaryRangeListPrefix,
      getValueOrBlank(employerInformation?.currentSalaryRange),
    );

    const newSalaryRange = fetchValueByKeyOfDropdown(
      programSiteCoreKeys.changeStatusModelPopupKey,
      programSiteCoreKeys.newSalaryRangeListPrefix,
      getValueOrBlank(employerInformation?.newSalaryRange),
    );

    const newLevelValue = fetchDropdownListKeyValueForCountrySpecific(
      programSiteCoreKeys.changeStatusModelPopupKey,
      programSiteCoreKeys.newLevelListPrefix,
      deliveryCountry,
      getValueOrBlank(employerInformation?.newLevel),
    );

    const newOccupationalValue = fetchDropdownListKeyValueForCountrySpecific(
      programSiteCoreKeys.changeStatusModelPopupKey,
      programSiteCoreKeys.newOccupationalFunctionListPrefix,
      deliveryCountry,
      getValueOrBlank(employerInformation?.newOccupationalFunction),
    );

    const newEmploymentContractTypeValue =
      fetchDropdownListKeyValueForCountrySpecific(
        programSiteCoreKeys.changeStatusModelPopupKey,
        programSiteCoreKeys.newEmploymentContractTypeListPrefix,
        deliveryCountry,
        getValueOrBlank(employerInformation?.newEmploymentContractType),
      );

    return {
      currentSalaryRange,
      newSalaryRange,
      newLevelValue: isNotEmpty(newLevelValue)
        ? newLevelValue
        : employerInformation?.newLevel,
      newOccupationalValue,
      newEmploymentContractTypeValue,
    };
  }, [employerInformation]);

  return (
    <MuiGrid
      container
      className="form-container grid-container"
      data-testid={`${pageId}-employee-information`}
    >
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(programSiteCoreKeys.newEmployerName)}
          replaceEmptyWithDash
          isValueWithEllipsis
          value={getValueOrBlank(employerInformation?.newEmployerName)}
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(programSiteCoreKeys.newTitle)}
          replaceEmptyWithDash
          isValueWithEllipsis
          value={getValueOrBlank(employerInformation?.newTitle)}
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(programSiteCoreKeys.functionalChange)}
          replaceEmptyWithDash
          isValueWithEllipsis
          value={fetchValueByKeyOfDropdown(
            programSiteCoreKeys.changeStatusModelPopupKey,
            programSiteCoreKeys.functionalChangeList,
            getValueOrBlank(employerInformation?.functionalChange),
          )}
        />
      </MuiGrid>
      {isFunctionalChange && (
        <>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(programSiteCoreKeys.newOccupationalFunction)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={countrySpecificParticipantDetail.newOccupationalValue}
            />
          </MuiGrid>

          {isNewOccupationalFunctionOther && (
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getLabel(
                  programSiteCoreKeys.newOccupationalFunctionOther,
                )}
                replaceEmptyWithDash
                isValueWithEllipsis
                value={getValueOrBlank(
                  employerInformation?.newOccupationalFunctionText,
                )}
              />
            </MuiGrid>
          )}
        </>
      )}

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(programSiteCoreKeys.occupationalLevelChangeStatus)}
          replaceEmptyWithDash
          isValueWithEllipsis
          value={fetchValueByKeyOfDropdown(
            programSiteCoreKeys.changeStatusModelPopupKey,
            programSiteCoreKeys.employerOccupationalLevelList,
            getValueOrBlank(employerInformation?.occupationalLevel),
          )}
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(programSiteCoreKeys.industryChange)}
          replaceEmptyWithDash
          isValueWithEllipsis
          value={fetchValueByKeyOfDropdown(
            programSiteCoreKeys.changeStatusModelPopupKey,
            programSiteCoreKeys.industryChangeList,
            getValueOrBlank(employerInformation?.industryChange),
          )}
        />
      </MuiGrid>
      {isIndustryChange && (
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.industryList)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={fetchValueByKeyOfDropdown(
              programSiteCoreKeys.changeStatusModelPopupKey,
              programSiteCoreKeys.industryList,
              getValueOrBlank(employerInformation?.industryList),
            )}
          />
        </MuiGrid>
      )}

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(programSiteCoreKeys.newJobStartDate)}
          replaceEmptyWithDash
          isValueWithEllipsis
          value={
            employerInformation?.newJobStartDate
              ? convertToUserFormatWithLocalTime(
                  employerInformation.newJobStartDate,
                  userCountryDateFormat,
                )
              : ""
          }
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(programSiteCoreKeys.compensationComparison)}
          replaceEmptyWithDash
          isValueWithEllipsis
          value={fetchValueByKeyOfDropdown(
            programSiteCoreKeys.changeStatusModelPopupKey,
            programSiteCoreKeys.compensationComparisonList,
            getValueOrBlank(employerInformation?.compansationComparision),
          )}
        />
      </MuiGrid>

      {currentSalaryRangeRule.visible && (
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.currentSalaryRange)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={countrySpecificParticipantDetail.currentSalaryRange}
          />
        </MuiGrid>
      )}

      {newSalaryRangeRule.visible && (
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.newSalaryRange)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={countrySpecificParticipantDetail.newSalaryRange}
          />
        </MuiGrid>
      )}

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(programSiteCoreKeys.placementMethod)}
          replaceEmptyWithDash
          isValueWithEllipsis
          value={fetchValueByKeyOfDropdown(
            programSiteCoreKeys.changeStatusModelPopupKey,
            programSiteCoreKeys.placementMethodList,
            getValueOrBlank(employerInformation?.placementMethod),
          )}
        />
      </MuiGrid>

      {newEmployerVATNumberRule.visible && (
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.newEmployerVATNumber)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={getValueOrBlank(employerInformation?.newEmployerVAT)}
          />
        </MuiGrid>
      )}

      {newLevelRule.visible && (
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.newLevel)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={countrySpecificParticipantDetail.newLevelValue}
          />
        </MuiGrid>
      )}
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(programSiteCoreKeys.workLocationType)}
          replaceEmptyWithDash
          isValueWithEllipsis
          value={fetchValueByKeyOfDropdown(
            programSiteCoreKeys.changeStatusModelPopupKey,
            programSiteCoreKeys.workLocationTypeList,
            getValueOrBlank(employerInformation?.workLocationType),
          )}
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(programSiteCoreKeys.dateOfEndOfTrialPeriod)}
          replaceEmptyWithDash
          isValueWithEllipsis
          value={
            employerInformation?.endDateOfTrialPeriod
              ? convertToUserFormatWithLocalTime(
                  employerInformation?.endDateOfTrialPeriod,
                  userCountryDateFormat,
                )
              : ""
          }
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(programSiteCoreKeys.solutionValidationDate)}
          replaceEmptyWithDash
          isValueWithEllipsis
          value={
            employerInformation?.solutionValidatedDate
              ? convertToUserFormatWithLocalTime(
                  employerInformation?.solutionValidatedDate,
                  userCountryDateFormat,
                )
              : ""
          }
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(programSiteCoreKeys.newEmploymentContractType)}
          replaceEmptyWithDash
          isValueWithEllipsis
          value={
            countrySpecificParticipantDetail.newEmploymentContractTypeValue
          }
        />
      </MuiGrid>
    </MuiGrid>
  );
};
